import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useAuthUser from "../../hooks/use-auth-user";
import Notifier from "../../modules/notifier/notifier";
import AppUser, { TAppUser } from "../../modules/app-user/app-user";
import RememberMe from "../../modules/remember-me";
import Utils from "../../utils/utils";
import ApiAuth from "../../modules/api/auth";
import usePageTitle from "../../hooks/use-page-title";
import useSessionConfig from "../../hooks/use-session-config";
import Config from "../../config/config";
import { ROUTES } from "../../routes";
import FGInput from "../../components/dumb/FGInput";
import FGToggleTextPassword from "../../components/dumb/FGToggleTextPassword";
import ApiARIAAuth from "../../modules/api/aria-auth";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import BasicAlert, { TBasicAlertRef } from "../../components/common/BasicAlert/BasicAlert";
import useFlashMessageStore from "../../hooks/use-flash-message-store";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

type TLoginFormData = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type TSubmitResult = { status: boolean; user?: TAppUser; message: string };

async function submit(formData: TLoginFormData, recaptchaV3Token: string): Promise<TSubmitResult> {
  if (formData.email === "" || formData.password === "") {
    return {
      status: false,
      message: "Email or password is required.",
    };
  }
  const params = {
    email: formData.email,
    password: formData.password,
    recaptchaV3Token: recaptchaV3Token,
  };
  const auth = await ApiAuth.login(params);

  if (!auth.status) {
    return {
      status: false,
      message: auth.message,
    };
  }


  const userId = auth.data._id;
  delete auth.data._id;

  const user = await AppUser.setInfo(
    Object.assign(
      {
        lastUpdate: Date.now(),
        id: userId,
        logged: true,
        email: formData.email,
        role: auth.data.roles.length === 1 ? auth.data.roles[0] : "",
        token: auth.data.authToken,

        just_logged: true,
        isAdfsUser: false,
      },
      auth.data
    )
  );

  const aria_token = await ApiARIAAuth.getToken();

  if (aria_token.status) {
    await AppUser.setInfo({
      aria_token: aria_token.data.accessToken ?? ""
    })
  }

  if (formData.rememberMe) {
    RememberMe.store(formData.email, formData.password);
  } else {
    RememberMe.remove();
  }

  return {
    status: true,
    user,
    message: auth.message,
  };
}

export default function PageLogin() {
  usePageTitle("Login");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const sessConfig = useSessionConfig();
  const auth = useAuthUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [formData, setFormData] = useState<TLoginFormData>({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [rememberMeInitialized, setRememberMeInitialized] = useState(false);
  const [isOpen, setIsOpen] = useState(() => {
    return localStorage.getItem("guest_login_collapsed") === "true";
  });

  const toggleAccordion = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      localStorage.setItem("guest_login_collapsed", String(newState));
      return newState;
    });
  };

  const adfsAuthUrl = `${process.env.REACT_APP_API_URL}/apiauth/adfs`;
  const basicAlertAdfs = useRef<TBasicAlertRef>();

  useFlashMessageStore("alert-login-adfs", (res) => basicAlertAdfs.current.show(res));

  const handleChange = function (e: any) {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((data) => ({ ...data, [e.target.name]: value }));
  };

  const onSubmit = async function (e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    const recaptchaV3Token = await executeRecaptcha("login");
    if (Config.IS_MOCKUP_MODE) {
      localStorage.setItem("mockup_user", formData.email);
      AppUser.getInfo().then((user) => {
        onSubmitted({
          status: true,
          message: `Logged as mockup user: ${user.role}`,
        });
      });
      return;
    }
    submit(formData, recaptchaV3Token).then(onSubmitted);
  };

  const onSubmitted = function (result: TSubmitResult) {
    if (result.status) {
      // Notifier.success(result.message);

      const previousPage = sessConfig.get("redirect", "");
      if (previousPage !== "") {
        sessConfig.remove("redirect");
      }

      setTimeout(() => {
        setSubmitting(false);
        if (result.user.role) {
          navigate(
            previousPage !== "" ? previousPage : ROUTES.PORTAL_DASHBOARD
          );
        } else {
          navigate(ROUTES.PORTAL_SELECT_ROLE);
        }
      }, 1000);
    } else {
      Notifier.error(result.message);
      setSubmitting(false);
    }
  };

  const loginByUser = async (user: any) => {
    await AppUser.setInfo(
      Object.assign(
        {
          lastUpdate: Date.now(),
          id: user._id,
          logged: true,
          token: user.authToken,
          just_logged: true,
        },
        user
      )
    );
  };

  useEffect(() => {
    if (Config.IS_MOCKUP_MODE) return;

    if (!auth.isLoading && auth.user.logged) {
      navigate(ROUTES.PORTAL_DASHBOARD);
    }
    let user = queryParams.get("user");
    if (user) loginByUser(JSON.parse(user));
  }, [auth]);

  useEffect(() => {
    const savedState = localStorage.getItem("guest_login_collapsed");
    if (savedState !== null) {
      setIsOpen(savedState === "true");
    }
  }, []);

  // initialize remember me
  useEffect(() => {
    RememberMe.get().then(async (data) => {
      if (data.checked) {
        // This sleep is intentional to avoid browser's autocomplete stuff
        // overwriting the fields' value
        await Utils.sleep(350);
        setFormData({
          email: data.email,
          password: data.password,
          rememberMe: data.checked,
        });
      }

      setRememberMeInitialized(true);
    });
  }, []);

  // if user unchecks remember me, then remove from storage
  useEffect(() => {
    if (rememberMeInitialized && !formData.rememberMe) {
      RememberMe.remove();
    }
  }, [rememberMeInitialized, formData]);

  return (
  <div className="d-flex flex-column justify-content-center align-items-center w-100 page-login-box-wrapper">
    <div className="PA__main">
      <div className="form-default">
          <h2 className="section-title">SIT Students and Staff</h2>
          <p className="text-center">(using your SIT Account credentials)</p>
          <p className="text-center m-0">
            <a href={adfsAuthUrl} className="btn btn-login-adfs">
              Login
            </a>
          </p>
          <BasicAlert ref={basicAlertAdfs} className='text-center' classNameChildren="mt-4" type='danger' noGap />
      </div>
    </div>
    <div className="PA__main">
      <form className="form-default" style={{position: "relative"}} onSubmit={onSubmit}>
          <h2 className="section-title">Industry Supervisors and Guests</h2>
          {!isOpen && <>
            <p className="text-center">(using your Company / GROW-Pro email address)</p>
            <p className="text-center m-0">
              <a onClick={toggleAccordion} className="btn btn-login-adfs">
                Click Here
              </a>
            </p>
          </>}
        <div style={{cursor: "pointer", position: "absolute", top: "-5px", right: "-5px", fontSize: "24px"}} onClick={toggleAccordion}>
          {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
        </div>
        {isOpen && (
          <>
            <FGInput
              label="Email"
              name="email"
              value={formData.email}
              placeholder=""
              onChange={(e) => handleChange(e)}
              autoComplete="off"
            />
            <FGToggleTextPassword
              name="password"
              label="Password"
              value={formData.password}
              placeholder=""
              onChange={(e) => handleChange(e)}
              autoComplete="off"
              />
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rememberMe"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={(e) => handleChange(e)}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember me
              </label>
            </div>
            <p className="text-center">
              <button type="submit" name="login" className="btn btn-secondary" disabled={submitting}>
                Login
              </button>
              </p>
            <p className="text-center mb-0">
              <Link to={ROUTES.AUTH_FORGOT_PASSWORD} className="link-grey">Forgot password?</Link>
            </p>
          </>
        )}
      </form>
    </div>
  </div>
  );
}
